/**
 * Partner Orders — a restaurant's own view of its partner-platform orders
 * (MenuSifu / eDiningExpress emails RushmyFood parses on this restaurant's
 * behalf), read-only.
 *
 * Deliberately NOT the super-admin's Courier Orders / Courier Inbox screens with
 * the guard rails removed — this is its own, smaller page. A restaurant can see
 * everything here (the same customer name/phone/address it already sees on its
 * own marketplace orders) but cannot dispatch, cancel, re-parse, or import
 * anything; those stay operational actions gated to admin. Only shows up with
 * data once a super admin links this restaurant to a Courier Merchant row on the
 * Courier Merchants screen — until then this is just an empty, harmless tab.
 */
import React, { useState, useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Chip, IconButton, CircularProgress, Alert,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider,
  Grid, Tooltip, Drawer, Stack, Avatar,
} from '@mui/material'
import DataTable from 'react-data-table-component'
import RefreshIcon from '@mui/icons-material/Refresh'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PersonIcon from '@mui/icons-material/Person'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'

const RESTAURANT_COURIER_ORDERS = gql`
  query RestaurantCourierOrders($restaurantId: ID) {
    restaurantCourierOrders(restaurantId: $restaurantId) {
      total
      orders {
        _id externalOrderNumber orderKind status
        customerName customerPhone
        dropoffAddress promiseTime dispatchAt
        subtotal deliveryFee processingFee tax tip total
        fdOrderId fdDriverName fdTrackerLink dispatchAttempts lastFdEventAt failureReason
        createdAt
        items { _id position title quantity unitPrice modifiers }
      }
    }
  }
`
const RESTAURANT_COURIER_EMAILS = gql`
  query RestaurantCourierEmails($restaurantId: ID) {
    restaurantCourierEmails(restaurantId: $restaurantId) {
      total
      countsByStatus
      emails {
        _id fromAddress subject receivedAt authResults rawHtml
        messageKind parserVersion confidence parsed detectedKind
        status reviewReason courierOrderId error createdAt
      }
    }
  }
`

const orderStatusStyle = {
  DRAFT:        { bg: '#f1f5f9', color: '#475569' },
  NEEDS_REVIEW: { bg: '#fef3c7', color: '#92400e' },
  READY:        { bg: '#e0f2fe', color: '#0369a1' },
  SCHEDULED:    { bg: '#e0e7ff', color: '#3730a3' },
  DISPATCHING:  { bg: '#fef9c3', color: '#713f12' },
  ASSIGNED:     { bg: '#dbeafe', color: '#1e40af' },
  PICKED_UP:    { bg: '#e0f2fe', color: '#0369a1' },
  EN_ROUTE:     { bg: '#ede9fe', color: '#5b21b6' },
  DELIVERED:    { bg: '#dcfce7', color: '#14532d' },
  CANCELLED:    { bg: '#fee2e2', color: '#991b1b' },
  FAILED:       { bg: '#fee2e2', color: '#991b1b' },
}
const emailStatusStyle = {
  RECEIVED:     { bg: '#e0f2fe', color: '#0369a1' },
  PARSED:       { bg: '#dcfce7', color: '#14532d' },
  NEEDS_REVIEW: { bg: '#fef3c7', color: '#92400e' },
  IMPORTED:     { bg: '#ede9fe', color: '#5b21b6' },
  IGNORED:      { bg: '#f1f5f9', color: '#475569' },
  FAILED:       { bg: '#fee2e2', color: '#991b1b' },
}
const kindStyle = {
  DELIVERY: { bg: '#dbeafe', color: '#1e40af' },
  PICKUP:   { bg: '#f0fdf4', color: '#166534' },
  TAKEOUT:  { bg: '#f0fdf4', color: '#166534' },
  DINE_IN:  { bg: '#f1f5f9', color: '#475569' },
  UNKNOWN:  { bg: '#fef3c7', color: '#92400e' },
}

const Pill = ({ label, styles }) => (
  <Chip size="small" label={label || '—'} sx={{ bgcolor: styles?.bg || '#f1f5f9', color: styles?.color || '#475569', fontWeight: 600 }} />
)

const PAGE_SIZE = 25
const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`)
const when = (s) => (s ? new Date(s).toLocaleString() : '—')
const minsSince = (s) => (s ? Math.round((Date.now() - new Date(s).getTime()) / 60000) : null)

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.2 }}>
    <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 500, wordBreak: 'break-word' }}>
      {value == null || value === '' ? <em style={{ color: '#94a3b8' }}>—</em> : String(value)}
    </Typography>
  </Box>
)

/** Read-only mirror of Courier Orders' detail drawer — no Dispatch/Cancel actions. */
const OrderDrawer = ({ order, onClose }) => {
  if (!order) return null
  return (
    <Drawer anchor="right" open={!!order} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>#{order.externalOrderNumber || order._id}</Typography>
            <Pill label={order.status} styles={orderStatusStyle[order.status]} />
            <Chip size="small" label={order.orderKind} sx={{ height: 22, fontSize: '0.7rem' }} />
          </Stack>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <Stack gap={2}>
          {order.failureReason && <Alert severity="error">{order.failureReason}</Alert>}

          <Box>
            <Typography variant="overline" color="text.secondary">Customer</Typography>
            <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32 }}><PersonIcon fontSize="small" /></Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.customerName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{order.customerPhone || 'no phone'}</Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Dropoff address</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{order.dropoffAddress || '—'}</Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Timing</Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Due</Typography>
              <Typography variant="body2">{when(order.promiseTime)}</Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Delivery status</Typography>
            <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: order.fdDriverName ? '#eff6ff' : undefined }}><LocalShippingIcon fontSize="small" /></Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.fdDriverName || (order.fdOrderId ? 'Driver not yet assigned' : 'Not dispatched yet')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {'last update '}{order.lastFdEventAt ? `${minsSince(order.lastFdEventAt)}m ago` : 'none yet'}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="overline" color="text.secondary">Items</Typography>
            <Stack gap={1} sx={{ mt: 0.5 }}>
              {(order.items || []).map((it) => {
                let mods = []
                try { mods = it.modifiers ? JSON.parse(it.modifiers) : [] } catch { mods = [] }
                return (
                  <Box key={it._id} sx={{ p: 1.25, borderRadius: 1, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.quantity}× {it.title}</Typography>
                      <Typography variant="body2">{money(it.unitPrice)}</Typography>
                    </Stack>
                    {mods.map((m, j) => (
                      <Typography key={j} variant="caption" color="text.secondary" display="block">– {m.title}{m.price != null ? ` (${money(m.price)})` : ''}</Typography>
                    ))}
                  </Box>
                )
              })}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Payment</Typography>
            <Stack gap={0.5} sx={{ mt: 0.5 }}>
              {[
                ['Subtotal', order.subtotal], ['Delivery fee', order.deliveryFee], ['Processing fee', order.processingFee],
                ['Tax', order.tax], ['Tip (driver)', order.tip],
              ].map(([l, v]) => (
                <Stack key={l} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{l}</Typography>
                  <Typography variant="body2">{money(v)}</Typography>
                </Stack>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Total</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{money(order.total)}</Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Drawer>
  )
}

export default function PartnerOrders() {
  const restaurantId = localStorage.getItem('restaurantId')
  const [tab, setTab] = useState(0)
  const [openOrderId, setOpenOrderId] = useState(null)
  const [openEmailId, setOpenEmailId] = useState(null)

  const ordersQ = useQuery(RESTAURANT_COURIER_ORDERS, {
    variables: { restaurantId: restaurantId || null }, fetchPolicy: 'cache-and-network',
  })
  const emailsQ = useQuery(RESTAURANT_COURIER_EMAILS, {
    variables: { restaurantId: restaurantId || null }, fetchPolicy: 'cache-and-network',
  })

  const orders = ordersQ.data?.restaurantCourierOrders?.orders ?? []
  const emails = emailsQ.data?.restaurantCourierEmails?.emails ?? []
  const openOrder = orders.find((o) => o._id === openOrderId) || null
  const openEmail = emails.find((e) => e._id === openEmailId) || null
  const parsed = useMemo(() => {
    try { return openEmail?.parsed ? JSON.parse(openEmail.parsed) : null } catch { return null }
  }, [openEmail])
  const validation = parsed?._validation

  const refetchAll = () => { ordersQ.refetch(); emailsQ.refetch() }
  const loading = ordersQ.loading || emailsQ.loading

  const orderColumns = [
    { name: 'Order #', width: '110px', cell: (r) => <strong>{r.externalOrderNumber || r._id}</strong> },
    { name: 'Status', width: '140px', cell: (r) => <Pill label={r.status} styles={orderStatusStyle[r.status]} /> },
    { name: 'Customer', width: '150px', cell: (r) => r.customerName || '—' },
    { name: 'Due', width: '160px', cell: (r) => <span style={{ fontSize: 13 }}>{when(r.promiseTime)}</span> },
    { name: 'Driver', width: '140px', cell: (r) => r.fdDriverName || (r.fdOrderId ? <span style={{ color: '#b45309' }}>none yet</span> : <span style={{ color: '#94a3b8' }}>—</span>) },
    { name: 'Total', width: '90px', cell: (r) => money(r.total) },
    {
      name: '', width: '60px',
      cell: (r) => <IconButton size="small" onClick={() => setOpenOrderId(r._id)}><VisibilityIcon fontSize="small" /></IconButton>,
    },
  ]

  const emailColumns = [
    { name: 'Received', width: '160px', cell: (r) => <span style={{ fontSize: 13 }}>{when(r.receivedAt)}</span> },
    { name: 'Subject', grow: 2, cell: (r) => <span style={{ fontSize: 13 }} title={r.subject}>{r.subject || '—'}</span> },
    { name: 'Kind', width: '130px', cell: (r) => <Pill label={r.detectedKind} styles={kindStyle[r.detectedKind]} /> },
    { name: 'Status', width: '150px', cell: (r) => <Pill label={r.status} styles={emailStatusStyle[r.status]} /> },
    { name: 'Why', grow: 2, cell: (r) => <span style={{ fontSize: 12, color: '#92400e' }}>{r.reviewReason || r.error || ''}</span> },
    {
      name: '', width: '60px',
      cell: (r) => <IconButton size="small" onClick={() => setOpenEmailId(r._id)}><VisibilityIcon fontSize="small" /></IconButton>,
    },
  ]

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <LocalShippingIcon sx={{ color: '#FF3008' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Partner Orders</Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14 }}>orders forwarded from your other online-ordering platform</Typography>
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={18} />}
        <IconButton onClick={refetchAll}><RefreshIcon /></IconButton>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label={`Orders (${orders.length})`} />
          <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label={`Incoming emails (${emails.length})`} />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Paper>
          <DataTable
            columns={orderColumns}
            data={orders}
            progressPending={ordersQ.loading}
            progressComponent={<Box sx={{ py: 4 }}><CircularProgress /></Box>}
            pagination
            paginationPerPage={PAGE_SIZE}
            noDataComponent={
              <Box sx={{ py: 5, color: '#64748b', textAlign: 'center', px: 3 }}>
                No partner orders yet. If you take orders through another online system as well
                as rushmyfood, ask support to link this restaurant so those orders show up here too.
              </Box>
            }
            onRowClicked={(r) => setOpenOrderId(r._id)}
            pointerOnHover highlightOnHover dense
          />
        </Paper>
      )}

      {tab === 1 && (
        <Paper>
          <DataTable
            columns={emailColumns}
            data={emails}
            progressPending={emailsQ.loading}
            progressComponent={<Box sx={{ py: 4 }}><CircularProgress /></Box>}
            pagination
            paginationPerPage={PAGE_SIZE}
            noDataComponent={<Box sx={{ py: 5, color: '#64748b' }}>No partner emails received yet</Box>}
            onRowClicked={(r) => setOpenEmailId(r._id)}
            pointerOnHover highlightOnHover dense
          />
        </Paper>
      )}

      <OrderDrawer order={openOrder} onClose={() => setOpenOrderId(null)} />

      {/* Read-only mirror of the Courier Inbox detail dialog — no Re-parse / Import / Ignore. */}
      <Dialog open={!!openEmail} onClose={() => setOpenEmailId(null)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <span>{openEmail?.subject || 'Email'}</span>
            <Pill label={openEmail?.status} styles={emailStatusStyle[openEmail?.status]} />
            <Pill label={openEmail?.detectedKind} styles={kindStyle[openEmail?.detectedKind]} />
          </Box>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
            from {openEmail?.fromAddress} · {when(openEmail?.receivedAt)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {validation?.blockers?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Still being reviewed before it&apos;s sent to a driver:</strong>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {validation.blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </Alert>
          )}
          {openEmail?.error && <Alert severity="error" sx={{ mb: 2 }}>{openEmail.error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>What we read</Typography>
              {!parsed && <Typography sx={{ color: '#64748b' }}>Not parsed yet.</Typography>}
              {parsed && (
                <>
                  <Field label="Order number" value={parsed.externalOrderNumber} />
                  <Field label="Order type" value={parsed.orderKind} />
                  <Field label="Promise time" value={parsed.promiseTimeRaw} />
                  <Divider sx={{ my: 1.5 }} />
                  <Field label="Customer" value={parsed.customer?.name} />
                  <Field label="Phone" value={parsed.customer?.phone} />
                  <Field label="Dropoff" value={parsed.address?.raw} />
                  <Divider sx={{ my: 1.5 }} />
                  <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', mb: 0.5 }}>Items</Typography>
                  {(parsed.items || []).map((it, i) => (
                    <Box key={i} sx={{ mb: 0.8 }}>
                      <Typography sx={{ fontSize: 14 }}>{it.quantity}× {it.title} <strong>{money(it.unitPrice)}</strong></Typography>
                      {(it.modifiers || []).map((m, j) => (
                        <Typography key={j} sx={{ fontSize: 13, color: '#475569', pl: 2 }}>– {m.title} {money(m.price)}</Typography>
                      ))}
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ fontSize: 14 }}>
                    {[
                      ['Sub total', parsed.money?.subTotal], ['Delivery fee', parsed.money?.deliveryFee],
                      ['Processing fee', parsed.money?.processingFee], ['Tax', parsed.money?.tax],
                      ['Tip (driver)', parsed.money?.tip], ['Total', parsed.money?.total],
                    ].map(([l, v]) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <span style={{ color: '#64748b' }}>{l}</span>
                        <strong>{money(v)}</strong>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>Original email</Typography>
                <Tooltip title="The email exactly as it arrived.">
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>(as received)</Typography>
                </Tooltip>
              </Box>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 560, overflow: 'auto', bgcolor: '#fff' }}>
                <iframe
                  title="original email"
                  sandbox=""
                  srcDoc={openEmail?.rawHtml || '<p style="font-family:sans-serif;color:#64748b">No HTML body</p>'}
                  style={{ width: '100%', height: 540, border: 0 }}
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmailId(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
